'use client';

import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faBuilding, faAward, faStar } from '@fortawesome/free-solid-svg-icons';

interface SejarahSectionClientProps {
    image?: string;
    contentIdn?: string;
    contentEn?: string;
    foundingYear?: string;
}

export default function SejarahSectionClient({ image, contentIdn, contentEn, foundingYear }: SejarahSectionClientProps) {
    const { t, language } = useTranslation();

    const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
    const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';

    return (
        <div className="flex flex-col lg:flex-row gap-12 items-center mb-20">
            <div className="lg:w-1/2" data-aos="fade-right">
                <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-br from-unmulYellow to-unmulGreen rounded-3xl opacity-20 blur-lg"></div>
                    <img src={image || "/img/placeholder/600x400.webp"} alt="Gedung UNMUL" className="relative rounded-2xl shadow-2xl w-full" />
                    <div className="absolute -bottom-6 -right-6 bg-unmulYellow text-unmulDarkGreen px-6 py-4 rounded-xl shadow-lg">
                        <span className="text-4xl font-bold">{foundingYear || '1962'}</span>
                        <p className="text-sm font-medium">{t('history.established') || 'Tahun Berdiri'}</p>
                    </div>
                </div>
            </div>

            <div className="lg:w-1/2" data-aos="fade-left">
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-1 w-12 bg-unmulYellow"></div>
                    <span className="text-unmulGreen font-bold tracking-widest uppercase text-sm">{t('history.label') || 'Awal Mula'}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                    {t('history.title') || 'Sejarah Singkat'} <span className="text-unmulGreen">{language === 'id' ? siteName : siteNameEnglish}</span>
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                    {language === 'id' ? (
                        contentIdn ? (
                            <div dangerouslySetInnerHTML={{ __html: contentIdn }} className="[&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-5 [&>ol]:list-decimal [&>ol]:ml-5" />
                        ) : (
                            <>
                                <p>
                                    <strong className="text-gray-800">Universitas Mulawarman</strong> didirikan pada tanggal <strong>27 September 1962</strong> berdasarkan Surat Keputusan Menteri Perguruan Tinggi dan Ilmu Pengetahuan (PTIP) No. 195 tahun 1962.
                                </p>
                                <p>
                                    Universitas ini dinamakan &quot;Mulawarman&quot; untuk mengenang nama Raja Mulawarman dari Kerajaan Kutai Kertanegara, kerajaan Hindu tertua di Indonesia yang berdiri pada abad ke-4 Masehi di wilayah Kalimantan Timur.
                                </p>
                                <p>
                                    Sebagai universitas negeri pertama dan terbesar di Kalimantan Timur, UNMUL memiliki peran strategis dalam pembangunan sumber daya manusia di kawasan timur Indonesia, khususnya dalam bidang studi tropis dan pengelolaan hutan hujan.
                                </p>
                            </>
                        )
                    ) : (
                        contentEn ? (
                            <div dangerouslySetInnerHTML={{ __html: contentEn }} className="[&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-5 [&>ol]:list-decimal [&>ol]:ml-5" />
                        ) : (
                            <>
                                <p>
                                    <strong className="text-gray-800">Mulawarman University</strong> was established on <strong>September 27, 1962</strong>, based on the Decree of the Minister of Higher Education and Science (PTIP) No. 195 of 1962.
                                </p>
                                <p>
                                    The university is named &quot;Mulawarman&quot; to commemorate King Mulawarman of the Kutai Martadipura Kingdom, the oldest Hindu kingdom in Indonesia, which existed in the 4th century AD in East Kalimantan.
                                </p>
                                <p>
                                    As the first and largest state university in East Kalimantan, UNMUL plays a strategic role in human resource development in eastern Indonesia, particularly in tropical studies and rainforest management.
                                </p>
                            </>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
